import { useRef, useState, useCallback, useEffect } from 'react';

const DEFAULT_MS = 6_000;

/**
 * A small, non-blocking, self-clearing toast — the same shape `useFreshness.js`
 * builds inline for its own "Updated with the latest data" pill, pulled out so
 * a second caller (the filter-cleanup notice) does not have to re-invent the
 * timer bookkeeping.
 *
 * Returns `[note, announce]`. `note` is `null` until `announce(text)` is
 * called, then holds `text` until `ms` elapses or `announce` is called again
 * (which restarts the clock rather than stacking two dismiss timers).
 */
export function useDismissingNote(ms = DEFAULT_MS) {
  const [note, setNote] = useState(null);
  const timer = useRef(null);

  const announce = useCallback((text) => {
    setNote(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setNote(null), ms);
  }, [ms]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return [note, announce];
}
